import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, History, Loader } from 'lucide-react';
import { HistoryYear } from '../../../../../services/historyService';
import styles from './AutofillDropdown.module.css';

interface AutofillDropdownProps {
  availableYears: HistoryYear[];
  isLoading: boolean;
  onSelectYear: (yearKey: string) => void;
  disabled?: boolean;
}

export const AutofillDropdown: React.FC<AutofillDropdownProps> = ({
  availableYears,
  isLoading,
  onSelectYear,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleYearClick = (yearKey: string) => {
    onSelectYear(yearKey);
    setIsOpen(false);
  };

  const hasYears = availableYears.length > 0;

  const getButtonTitle = () => {
    if (isLoading) return 'Loading history...';
    if (!hasYears) return 'No previous years with selections';
    return 'Copy songs from a previous year';
  };

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        type="button"
        className={styles.triggerButton}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isLoading || !hasYears}
        title={getButtonTitle()}
      >
        {isLoading ? (
          <Loader size={16} className={styles.spinner} />
        ) : (
          <History size={16} />
        )}
        <span>Copy from Previous Year</span>
        <ChevronDown size={14} className={isOpen ? styles.chevronOpen : ''} />
      </button>

      {isOpen && hasYears && (
        <div className={styles.dropdown}>
          <div className={styles.header}>Select Year to Copy</div>
          {availableYears.map(year => (
            <button
              key={year.liturgicalYear}
              className={styles.yearOption}
              onClick={() => handleYearClick(year.liturgicalYear)}
              type="button"
            >
              <span className={styles.yearName}>
                {year.yearLetter ? `Year ${year.yearLetter}` : year.liturgicalYear}
                {year.liturgyDate && (
                  <span className={styles.yearDate}> ({year.liturgyDate})</span>
                )}
              </span>
              <span className={styles.songCount}>
                {year.songSelections.length} song{year.songSelections.length !== 1 ? 's' : ''}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
